import {
  Button,
  Card,
  Grid2,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { TbArrowBackUp } from "react-icons/tb";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import BillingDetails from "./BillingDetails";
import BrokerPreference from "./BrokerPreference";
import Dividend from "./Dividend";
import ExecutedOrder from "./ExecutedOrder";
import InvoiceCard from "./InvoiceCard";
import MicroWebsite from "./MicroWebsite";
import Overview from "./Overview";
import PaymentGateway from "./PaymentGateway";
import PortfolioOfferings from "./PortfolioOfferings";
import Revenue from "./Revenue";
import UniqueBrokerCount from "./UniqueBrokerCount";
import WalletTopup from "./WalletTopup";
import PortfolioLimits from "./portfolioLimits";

const Preference = () => {
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState(0);

  const navigate = useNavigate();

  const { data, refetch, isFetched } = useQuery({
    queryKey: ["GET_BROKER"],
    queryFn: () =>
      axiosInstance.get(`/super_admin/${id}/get_broker_integration_status/`),
  });

  const { data: adminDetails } = useQuery({
    queryKey: ["GET_ADMIN_DETAILS"],
    queryFn: () => axiosInstance.get(`/super_admin/${id}/advisor-details/`),
  });

  const { data: revenueData, refetch: revenueRefetch } = useQuery({
    queryKey: ["GET_REVENUE_DETAILS", id],
    queryFn: () => axiosInstance.get(`/super_admin/${id}/get-revenue/`),
    enabled: !!id,
  });

  const { data: invoiceSeriesData, refetch: invoiceSeriesRefetch } = useQuery({
    queryKey: ["GET_INVOICE_SERIES", "7016303725"],
    queryFn: () => axiosInstance.get(`/super_admin/${id}/get_invoice_series/`),
    enabled: true,
  });

  return (
    <DashboardLayout
      title="Preference"
      toolbar={
        <Button size="extraSmall" onClick={() => navigate(-1)}>
          Back &nbsp; <TbArrowBackUp />
        </Button>
      }
    >
      <Grid2 size={12}>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                {/* Advisor Name */}
                <TableCell
                  width="20%"
                  sx={{
                    p: 2,
                    borderBottom: "none",
                    verticalAlign: "top",
                    borderRight: "1px solid var(--grey-100)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "secondary.main",
                      fontWeight: 500,
                      fontSize: 12,
                      mb: 0.5,
                    }}
                  >
                    Advisor Name
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: 18,
                      color: "var(--primary-900)",
                    }}
                  >
                    {adminDetails?.data?.data?.advisorName || "-"}
                  </Typography>
                </TableCell>

                {/* Company Name */}
                <TableCell
                  width="20%"
                  sx={{
                    p: 2,
                    borderBottom: "none",
                    verticalAlign: "top",
                    borderRight: "1px solid var(--grey-100)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "secondary.main",
                      fontWeight: 500,
                      fontSize: 12,
                      mb: 0.5,
                    }}
                  >
                    Company Name
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: 18,
                      color: "var(--primary-900)",
                    }}
                  >
                    {adminDetails?.data?.data?.companyName || "-"}
                  </Typography>
                </TableCell>

                {/* Phone Number */}
                <TableCell
                  width="20%"
                  sx={{
                    p: 2,
                    borderBottom: "none",
                    verticalAlign: "top",
                    borderRight: "1px solid var(--grey-100)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "secondary.main",
                      fontWeight: 500,
                      fontSize: 12,
                      mb: 0.5,
                    }}
                  >
                    Phone Number
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: 18,
                      color: "var(--primary-900)",
                    }}
                  >
                    {adminDetails?.data?.data?.phoneNumber || "-"}
                  </Typography>
                </TableCell>

                {/* Total Portfolio */}
                <TableCell
                  width="20%"
                  sx={{
                    p: 2,
                    borderBottom: "none",
                    verticalAlign: "top",
                    borderRight: "1px solid var(--grey-100)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "secondary.main",
                      fontWeight: 500,
                      fontSize: 12,
                      mb: 0.5,
                    }}
                  >
                    Total Portfolio
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: 18,
                      color: "var(--primary-900)",
                    }}
                  >
                    {adminDetails?.data?.data?.totalPortfolio ?? "-"}
                  </Typography>
                </TableCell>

                {/* Portfolio Limit */}
                <TableCell
                  width="20%"
                  sx={{
                    p: 2,
                    borderBottom: "none",
                    verticalAlign: "top",
                    borderRight: "1px solid var(--grey-100)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "secondary.main",
                      fontWeight: 500,
                      fontSize: 12,
                      mb: 0.1,
                    }}
                  >
                    Portfolio Limit
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: 18,
                      color: "var(--primary-900)",
                    }}
                  >
                    {adminDetails?.data?.data?.portfolioLimit ?? "-"}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid2>

      <br />
      <Card>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Tabs
              variant="standard"
              value={activeTab}
              onChange={(e, val) => {
                setActiveTab(val);
              }}
            >
              <Tab disableRipple label="Overview" />
              <Tab disableRipple label="Offerings" />
              <Tab disableRipple label="Integrations" />
              <Tab disableRipple label="Broker Preference" />
              <Tab disableRipple label="Revenue" />
              <Tab disableRipple label="Invoice" />
              <Tab disableRipple label="Wallet" />
            </Tabs>
          </Grid2>

          <Grid2 size={12}>
            {activeTab === 0 && <Overview />}
            {activeTab === 1 && (
              <>
                <PortfolioOfferings />
                <br />
                <MicroWebsite />
              </>
            )}
            {activeTab === 2 && (
              <>
                <ExecutedOrder />
                <br />
                <Dividend data={data} isFetched={isFetched} refetch={refetch} />
                <br />
                <PaymentGateway
                  data={data}
                  isFetched={isFetched}
                  refetch={refetch}
                />
              </>
            )}
            {activeTab === 3 && (
              <>
                <BrokerPreference
                  data={data}
                  isFetched={isFetched}
                  refetch={refetch}
                />
                <br />
                <UniqueBrokerCount />
              </>
            )}
            {activeTab === 4 && (
              <>
                <Revenue
                  revenueData={revenueData}
                  revenueRefetch={revenueRefetch}
                />
                <br />
                <PortfolioLimits />
              </>
            )}
            {activeTab === 5 && (
              <>
                <InvoiceCard
                  invoiceSeriesData={invoiceSeriesData}
                  invoiceSeriesRefetch={invoiceSeriesRefetch}
                />
                <br />
                <BillingDetails
                  revenueData={revenueData}
                  revenueRefetch={revenueRefetch}
                />
              </>
            )}
            {activeTab === 6 && (
              <>
                <WalletTopup />
              </>
            )}
          </Grid2>
        </Grid2>
      </Card>
    </DashboardLayout>
  );
};

export default Preference;
